import { renderSettings } from "./renderSettings";
/**
 * Constantes globales. Aucune logique ici : uniquement des valeurs lisibles
 * et modifiables sans rien casser ailleurs.
 */

/**
 * `?debug=1` permet d'activer le debug sur un build de prod servi à un
 * téléphone réel, là où import.meta.env.DEV est faux.
 */
const debugFromUrl = (): boolean => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("debug");
};

export const config = {
  render: renderSettings,

  /**
   * L'économie d'une partie ([GD-073]). Les montants de départ se choisissent
   * au curseur ; ce qui est ici ne se choisit pas encore.
   */
  economy: {
    /**
     * Or touché par chaque camp à chaque tour, avant tout bonus de moral. Ce
     * qui pourrait le faire monter n'est pas tranché : c'est un socle, pas une
     * règle de croissance.
     */
    income: 20,
  },

  // `import.meta.env` n'existe pas hors de Vite : lu sans précaution, il fait
  // échouer tout script Node important ce fichier (voir [TD-021]).
  debug: (import.meta.env?.DEV ?? false) || debugFromUrl(),

  /** Sélecteur du conteneur du canvas dans index.html. */
  containerSelector: "#app",

  loop: {
    /**
     * Un onglet en arrière-plan ou un breakpoint produit un dt énorme au
     * retour ; on le plafonne pour éviter le saut d'intégration.
     */
    maxDeltaTime: 0.1,
  },

  input: {
    /**
     * Vitesse de pointeur (px/s) qui correspond à look = 1. Plus la valeur
     * est haute, plus il faut bouger vite pour saturer l'axe ; en dessous
     * de cette vitesse rien n'est perdu par le clamp du contrat [-1, 1].
     */
    lookRateReference: 6000,
    /** Idem pour le zoom, en px de molette (ou d'écartement de pinch) par seconde. */
    zoomRateReference: 1500,
    /**
     * Idem pour le glissement d'un doigt. Ne joue qu'en dessous : un doigt
     * parcourt 300 à 2 000 px/s, donc `move` sature à 1 dans presque tout
     * geste réel, et c'est `camera.panSpeed` qui fixe alors la vitesse. Cette
     * référence ne garde du sens que pour les glissements très lents, où elle
     * préserve la finesse ([GD-045]).
     */
    moveRateReference: 300,
    /**
     * Pixels équivalents à un radian de rotation à deux doigts. Choisi pour
     * que le monde tourne d'autant que les doigts, avec `lookSpeed` et
     * `lookRateReference` ci-dessus.
     */
    pinchTurnPixels: 320,

    /**
     * Gain propre au pincement. Deux doigts écartent moins de pixels qu'une
     * molette ne délivre de crans : sans ce gain, zoomer au doigt demande
     * plusieurs gestes là où la souris en demande un.
     */
    pinchZoomGain: 1.25,

    /**
     * Au doigt, au-delà de ce déplacement (px) ou de cette durée (ms), un appui
     * devient un balayage et ne désigne plus rien ([TD-041]). La souris n'en a
     * pas besoin : son clic gauche n'a pas d'autre fonction ([TD-044]).
     */
    touchTapMaxDistance: 12,
    tapMaxDuration: 400,
    /**
     * Un cran de molette arrive entièrement dans une frame : converti tel quel
     * en vitesse, il dépasse le contrat [-1, 1] et se fait tronquer, donc un
     * gros cran zoome autant qu'un petit. On l'étale sur cette durée (s).
     */
    wheelReleaseTime: 0.12,
  },

  camera: {
    fov: 55,
    near: 0.1,
    far: 1000,
    /** Distance initiale au pivot, en mètres. Cadrée sur la carte entière (~78 x 98 m). */
    distance: 120,
    /** Tangage initial, en radians. Vue haute, adaptée à la lecture d'une carte. */
    pitch: 0.9,
    minDistance: 4,
    maxDistance: 700,
    /**
     * Bornes de tangage, en radians. Le minimum est positif : en dessous, la
     * caméra passerait sous le plan du terrain ([TD-040]).
     */
    minPitch: 0.06,
    maxPitch: 1.45,
    /**
     * Altitude minimale de la caméra, en mètres, mesurée en absolu et non par
     * rapport au sol visé.
     *
     * Dérivée du plafond du brouillard ([GD-066]) : la masse de parchemin est
     * un volume plein montant à `fog.ceiling`, et une caméra plus basse y entre
     * — on se retrouve alors à l'intérieur, à voir la carte par en dessous. La
     * dériver plutôt que de la recopier interdit aux deux de diverger.
     *
     * La marge est faible à dessein : chaque centimètre gagné ici est de la
     * distance minimale perdue à angle rasant, où elle vaut `altitude / sin`.
     */
    minAltitude: renderSettings.fog.ceiling + 0.3,
    /** Vitesses appliquées à un input normalisé, en rad/s et m/s. */
    lookSpeed: 19,
    /** Mis à l'échelle de la carte : un cran de molette vaut environ 14 m. */
    zoomSpeed: 210,
    /**
     * Vitesse de translation à la distance de référence ci-dessus ([GD-044]).
     * C'est elle, et non `input.moveRateReference`, qui règle la vitesse d'un
     * glissement au doigt : celui-ci sature le contrat d'entrée.
     */
    panSpeed: 135,
    /**
     * De combien le point observé peut sortir de la carte, en fraction de la
     * distance de vue. Proportionnel plutôt que fixe : à 120 m on voit large et
     * on veut pouvoir dégager la vue, à 10 m un même débord sortirait la carte
     * de l'écran. Le zoom, lui, ne vise jamais hors de la carte ([GD-019]).
     */
    edgeMargin: 0.25,
    /** Lissage exponentiel : plus grand = plus sec. */
    damping: 12,
  },

  map: {
    /** Même graine, même carte, toujours ([TD-020]). */
    seed: 1914,

    /** Carte en parallélogramme ([TD-027]) : W tuiles par rangée. */
    width: 45,
    /**
     * Nombre de rangées. Ramené au nombre impair supérieur si besoin : la
     * rangée centrale porte la ligne de front et doit exister ([TD-039]).
     * Le découpage en trois zones s'en déduit ([TD-023]).
     */
    length: 65,

    /** Rayon d'une tuile en mètres, du centre à un sommet. Valeur arbitraire. */
    hexSize: 1,

    /**
     * Deux bruits indépendants ([TD-034]). La fréquence est en unités monde :
     * plus elle est basse, plus les amas sont larges. À 0,14 la longueur d'onde
     * valait quatre tuiles sur une carte de quarante-cinq, d'où une mosaïque
     * uniforme au lieu de massifs.
     */
    noise: {
      // Fréquence très basse assumée : elle donne de larges massifs. Elle
      // privait autrefois un camp de tout un terrain, ce que [TD-038] a réglé
      // en égalisant les compositions — le compromis d'alors n'a plus lieu d'être.
      relief: {
        frequency: 0.03,
        octaves: 4,
        persistence: 0.6,
        lacunarity: 2,
      },
      vegetation: {
        frequency: 0.035,
        octaves: 3,
        persistence: 0.35,
        lacunarity: 2,
      },
    },

    /**
     * Anneaux de côte en partant du bord. Zéro sur la carte de référence
     * ([TD-027]) ; conservé pour d'autres formes de carte.
     */
    coastFalloffRings: 0,

    balance: {
      /** Cartes candidates générées à chaque appel ([TD-025]). */
      candidates: 20,

      /** Au-delà de ce coût fini, une tuile est « coûteuse » plutôt que « facile ». */
      costlyThreshold: 2,

      /** Une montagne mal placée pèse plus qu'une colline mal placée. */
      weights: {
        impassable: 3,
        costly: 1,
      },

      /**
       * Diagnostic uniquement, jamais utilisé pour rejeter ([TD-025]).
       * Deux entrées et non trois : les trois écarts d'une bande sont liés par
       * somme nulle. À recaler après tout changement de taille ou de bruit,
       * des amas plus larges creusant mécaniquement les écarts.
       */
      tolerance: {
        impassable: 10,
        costly: 13,
      },
    },

    /**
     * ALTITUDE de la face supérieure de chaque niveau, index = niveau, en
     * mètres. Ce ne sont PAS des hauteurs de prisme : la plaine est à 0, lue
     * comme une hauteur elle serait d'épaisseur nulle donc invisible. Un prisme
     * monte d'une base commune sous le minimum, sa hauteur vaut altitude - base.
     */
    elevationHeights: [-0.2, 0, 0.6, 1.8],
  },

  device: {
    /** Plafonds de devicePixelRatio ; au-delà le gain visuel ne paie pas le coût. */
    maxPixelRatioDesktop: 2,
    maxPixelRatioMobile: 1.5,
  },
} as const;

/** Réglages de bruit mutables, point de départ du panneau de debug ([TD-036]). */
export interface NoiseSettings {
  frequency: number;
  octaves: number;
  persistence: number;
  lacunarity: number;
}

export interface MapNoiseSettings {
  relief: NoiseSettings;
  vegetation: NoiseSettings;
}

/**
 * Dimensions mutables, réglables à chaud ([TD-036]). Le découpage en zones n'y
 * figure pas : il se déduit de la longueur, voir `zoneLayout`.
 */
export interface MapShapeSettings {
  width: number;
  length: number;
}

export const defaultShapeSettings = (): MapShapeSettings => ({
  width: config.map.width,
  length: config.map.length,
});

export const defaultNoiseSettings = (): MapNoiseSettings => ({
  relief: { ...config.map.noise.relief },
  vegetation: { ...config.map.noise.vegetation },
});
